<template>
  <div>
    <b-card no-body>
      <b-card-body>
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h5 class="mb-0">{{ $t("dmp.networkOptions.title") }}</h5>
          <div class="d-flex gap-1">
            <b-button
              v-if="!isTemplateMode"
              variant="primary"
              size="sm"
              @click="saveNetworkOptions"
              :disabled="isSaving"
            >
              <b-spinner v-if="isSaving" small class="mr-25" />
              <feather-icon v-else icon="SaveIcon" size="14" class="mr-25" />
              {{ isSaving ? $t("actions.saving") : $t("actions.save") }}
            </b-button>
          </div>
        </div>

        <!-- Loading spinner -->
        <div v-if="isLoading" class="d-flex justify-content-center p-3">
          <b-spinner
            variant="primary"
            :label="$t('dmp.networkOptions.loadingNetworkOptions')"
          />
          <span class="ml-2">{{ $t("dmp.networkOptions.loadingNetworkOptions") }}</span>
        </div>

        <validation-observer ref="refFormObserver" v-else>
          <b-form @submit.prevent="saveNetworkOptions">
            <b-row>
              <!-- Options Section -->
              <b-col cols="12">
                <b-card class="mb-2 border">
                  <h6 class="mb-2 font-weight-bold">
                    {{ $t("dmp.networkOptions.options") }}
                  </h6>

                  <b-row>
                    <b-col cols="12" md="6">
                      <b-form-checkbox v-model="networkOptions.dhcp" class="mb-75">
                        DHCP Enable
                      </b-form-checkbox>

                      <small class="text-warning d-flex align-items-center mb-1">
                        <feather-icon icon="AlertTriangleIcon" size="14" class="mr-50" />
                        <span>{{ $t("dmp.networkOptions.dhcpWarning") }}</span>
                      </small>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col cols="12" md="6">
                      <b-form-group
                        :label="$t('dmp.networkOptions.localIp')"
                        label-for="localIp"
                      >
                        <ip-address-input
                          :value="networkOptions.localIp"
                          @input="(val) => (networkOptions.localIp = val)"
                          :disabled="networkOptions.dhcp"
                        />
                      </b-form-group>
                    </b-col>

                    <b-col cols="12" md="6">
                      <b-form-group
                        :label="$t('dmp.networkOptions.gateway')"
                        label-for="gateway"
                      >
                        <ip-address-input
                          :value="networkOptions.gateway"
                          @input="(val) => (networkOptions.gateway = val)"
                          :disabled="networkOptions.dhcp"
                        />
                      </b-form-group>
                    </b-col>

                    <b-col cols="12" md="6">
                      <b-form-group
                        :label="$t('dmp.networkOptions.subnetMask')"
                        label-for="subnetMask"
                      >
                        <ip-address-input
                          :value="networkOptions.subnetMask"
                          @input="(val) => (networkOptions.subnetMask = val)"
                          :disabled="networkOptions.dhcp"
                        />
                      </b-form-group>
                    </b-col>

                    <b-col cols="12" md="6">
                      <b-form-group
                        :label="$t('dmp.networkOptions.dnsServer')"
                        label-for="dnsServer"
                      >
                        <ip-address-input
                          :value="networkOptions.dnsServer"
                          @input="(val) => (networkOptions.dnsServer = val)"
                          :disabled="networkOptions.dhcp"
                        />
                      </b-form-group>
                    </b-col>

                    <b-col cols="12" md="6">
                      <validation-provider
                        #default="validationContext"
                        :name="$t('dmp.networkOptions.listenPort734n')"
                        rules="required|integer|min_value:1|max_value:65535"
                      >
                        <b-form-group
                          :label="$t('dmp.networkOptions.listenPort734n')"
                          label-for="listenPort734nOptions"
                        >
                          <b-form-input
                            id="listenPort734nOptions"
                            v-model="networkOptions.listenPort734nOptions"
                            type="text"
                            maxlength="5"
                            :state="getValidationState(validationContext)"
                          />
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <b-col cols="12" md="6">
                      <validation-provider
                        #default="validationContext"
                        :name="$t('dmp.networkOptions.passphrase734n')"
                        rules="max:32"
                      >
                        <b-form-group
                          :label="$t('dmp.networkOptions.passphrase734n')"
                          label-for="passphrase734n"
                        >
                          <b-input-group
                            class="input-group-merge"
                            :class="getValidationState(validationContext) === false ? 'is-invalid' : ''"
                          >
                            <b-form-input
                              id="passphrase734n"
                              v-model="networkOptions.passphrase734n"
                              :type="passphrase734nFieldType"
                              maxlength="32"
                              :state="getValidationState(validationContext)"
                            />
                            <b-input-group-append is-text>
                              <feather-icon
                                class="cursor-pointer"
                                :icon="passphrase734nFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'"
                                @click="passphrase734nFieldType = passphrase734nFieldType === 'password' ? 'text' : 'password'"
                              />
                            </b-input-group-append>
                          </b-input-group>
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>
                  </b-row>
                </b-card>
              </b-col>

              <!-- Wi-Fi Settings Section -->
              <b-col cols="12">
                <b-card class="mb-2 border">
                  <h6 class="mb-2 font-weight-bold">
                    {{ $t("dmp.networkOptions.wifiSettings") }}
                  </h6>

                  <b-row>
                    <b-col cols="12" md="6">
                      <validation-provider
                        #default="validationContext"
                        :name="$t('dmp.networkOptions.ssid')"
                        rules="required|max:32"
                      >
                        <b-form-group
                          :label="$t('dmp.networkOptions.ssid')"
                          label-for="ssid"
                        >
                          <b-form-input
                            id="ssid"
                            v-model="networkOptions.ssid"
                            type="text"
                            maxlength="32"
                            :state="getValidationState(validationContext)"
                          />
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <b-col cols="12" md="6">
                      <validation-provider
                        #default="validationContext"
                        :name="$t('dmp.networkOptions.security')"
                        rules="required"
                      >
                        <b-form-group
                          :label="$t('dmp.networkOptions.security')"
                          label-for="wifiSecurityScheme"
                        >
                          <b-form-select
                            id="wifiSecurityScheme"
                            v-model="networkOptions.wifiSecurityScheme"
                            :options="securityOptions"
                            :state="getValidationState(validationContext)"
                          />
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <b-col cols="12" md="6">
                      <validation-provider
                        #default="validationContext"
                        name="Passphrase"
                        rules="max:32"
                      >
                        <b-form-group
                          label="Passphrase"
                          label-for="wifiPassword"
                        >
                          <b-input-group
                            class="input-group-merge"
                            :class="getValidationState(validationContext) === false ? 'is-invalid' : ''"
                          >
                            <b-form-input
                              id="wifiPassword"
                              v-model="networkOptions.wifiPassword"
                              :type="wifiPasswordFieldType"
                              maxlength="32"
                              :state="getValidationState(validationContext)"
                            />
                            <b-input-group-append is-text>
                              <feather-icon
                                class="cursor-pointer"
                                :icon="wifiPasswordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'"
                                @click="wifiPasswordFieldType = wifiPasswordFieldType === 'password' ? 'text' : 'password'"
                              />
                            </b-input-group-append>
                          </b-input-group>
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>
                  </b-row>
                </b-card>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  ref,
  onMounted,
  onBeforeUnmount,
  inject,
  getCurrentInstance,
  computed,
  watch,
} from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import { useTemplateMode } from "./useTemplateMode";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { required, integer } from "@validations";
import formValidation from "@core/comp-functions/forms/form-validation";
import IpAddressInput from "@/components/InputIp/IpAddressInput.vue";

export default {
  name: "ConfigNetworkOptions",
  props: {
    uuid: {
      type: String,
      required: true,
    },
    panelId: {
      type: [Number, String],
      required: true,
    },
    networkOptionsData: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    source: {
      type: String,
      default: "default",
    },
  },
  components: {
    ValidationObserver,
    ValidationProvider,
    IpAddressInput,
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance();
    const proxy = vm.root.proxy;
    const { t } = useI18nUtils();
    const toast = useToast();

    const { isTemplateMode, emitTemplateUpdate } = useTemplateMode(props, emit);
    const isLoading = computed(() => props.loading);
    const isSaving = ref(false);
    const isSyncing = ref(false);
    const passphrase734nFieldType = ref("password");
    const wifiPasswordFieldType = ref("password");
    const { refFormObserver, getValidationState } = formValidation();

    const networkOptions = ref({
      // Options
      dhcp: false,
      localIp: "0.0.0.0",
      gateway: "0.0.0.0",
      subnetMask: "255.255.255.0",
      encryptionPassphrase: "",
      dnsServer: "0.0.0.0",
      listenPort734nOptions: "2002",
      passphrase734n: "",

      // Wi-Fi Settings
      ssid: "",
      wifiSecurityScheme: "NONE",
      wifiPassword: "",
    });

    const securityOptions = [
      { value: "NONE", text: proxy.$t("dmp.networkOptions.securityNone") },
      { value: "WEP", text: proxy.$t("dmp.networkOptions.securityWep") },
      { value: "WPA", text: proxy.$t("dmp.networkOptions.securityWpa") },
    ];

    const cleanString = (str) => {
      if (!str || typeof str !== "string") return str;
      return str
        .replace(/\u001e/g, "")
        .replace(/^-+/, "")
        .trim();
    };

    const normalizeIP = (ip) => {
      if (!ip || typeof ip !== "string") return ip;
      return ip
        .split(".")
        .map((octet) => parseInt(octet, 10).toString())
        .join(".");
    };

    const mapSecurityScheme = (scheme) => {
      const schemeMap = {
        0: "WEP",
        1: "WPA",
        2: "NONE",
      };
      return schemeMap[scheme] || scheme;
    };

    const loadNetworkOptions = () => {
      if (!props.networkOptionsData) {
        return;
      }

      try {
        const data = props.networkOptionsData;

        networkOptions.value = {
          dhcp: data.dhcp === "Y",
          localIp: normalizeIP(data.localIp) || networkOptions.value.localIp,
          gateway: normalizeIP(data.gateway) || networkOptions.value.gateway,
          subnetMask: normalizeIP(data.subnetMask) || networkOptions.value.subnetMask,
          dnsServer: normalizeIP(data.dnsServer) || networkOptions.value.dnsServer,
          encryptionPassphrase: cleanString(data.encryptionPassphrase) || "",
          listenPort734nOptions: data.listenPort734nOptions
            ? parseInt(data.listenPort734nOptions, 10).toString()
            : networkOptions.value.listenPort734nOptions,
          passphrase734n: data.passphrase734n || "",
          ssid: cleanString(data.ssid) || "",
          wifiSecurityScheme: mapSecurityScheme(data.wifiSecurityScheme) || "NONE",
          wifiPassword: cleanString(data.wifiPassword) || "",
        };
      } catch (error) {
        console.error("Error loading network options:", error);
      }
    };

    // Construye la sección en forma GET para el template.
    const buildTemplateItem = () => {
      const original = props.networkOptionsData || {};
      // Keep the panel-encoded original (padding / leading dashes) when the
      // visible value was not actually changed.
      const keepIfUnchanged = (origVal, formVal) =>
        cleanString(origVal) === formVal ? origVal : formVal;
      const secRev = { WEP: "0", WPA: "1", NONE: "2" };

      const item = {
        ...original,
        dhcp: networkOptions.value.dhcp ? "Y" : "N",
        localIp: networkOptions.value.localIp,
        gateway: networkOptions.value.gateway,
        subnetMask: networkOptions.value.subnetMask,
        dnsServer: networkOptions.value.dnsServer,
        listenPort734nOptions: networkOptions.value.listenPort734nOptions,
        ssid: keepIfUnchanged(original.ssid, networkOptions.value.ssid),
        wifiSecurityScheme:
          secRev[networkOptions.value.wifiSecurityScheme] ??
          original.wifiSecurityScheme ??
          "2",
        wifiPassword: keepIfUnchanged(
          original.wifiPassword,
          networkOptions.value.wifiPassword
        ),
      };
      if (networkOptions.value.encryptionPassphrase) {
        item.encryptionPassphrase = keepIfUnchanged(
          original.encryptionPassphrase,
          networkOptions.value.encryptionPassphrase
        );
      }
      if (networkOptions.value.passphrase734n) {
        item.passphrase734n = networkOptions.value.passphrase734n;
      }
      return item;
    };

    const validateTemplateSection = async () =>
      refFormObserver.value ? await refFormObserver.value.validate() : true;

    const saveNetworkOptions = async () => {
      const isValid = await refFormObserver.value.validate();
      if (!isValid) {
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.networkOptions.messages.validationError"),
            text: proxy.$t("dmp.networkOptions.messages.validationErrorText"),
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
        return;
      }

      if (isTemplateMode.value) {
        const original = props.networkOptionsData || {};
        // Keep the panel-encoded original ( padding / leading dashes)
        // when the visible value was not actually changed.
        const keepIfUnchanged = (origVal, formVal) =>
          cleanString(origVal) === formVal ? origVal : formVal;
        const secRev = { WEP: "0", WPA: "1", NONE: "2" };

        const item = {
          ...original,
          dhcp: networkOptions.value.dhcp ? "Y" : "N",
          localIp: networkOptions.value.localIp,
          gateway: networkOptions.value.gateway,
          subnetMask: networkOptions.value.subnetMask,
          dnsServer: networkOptions.value.dnsServer,
          listenPort734nOptions: networkOptions.value.listenPort734nOptions,
          ssid: keepIfUnchanged(original.ssid, networkOptions.value.ssid),
          wifiSecurityScheme:
            secRev[networkOptions.value.wifiSecurityScheme] ??
            original.wifiSecurityScheme ??
            "2",
          wifiPassword: keepIfUnchanged(
            original.wifiPassword,
            networkOptions.value.wifiPassword
          ),
        };
        if (networkOptions.value.encryptionPassphrase) {
          item.encryptionPassphrase = keepIfUnchanged(
            original.encryptionPassphrase,
            networkOptions.value.encryptionPassphrase
          );
        }
        if (networkOptions.value.passphrase734n) {
          item.passphrase734n = networkOptions.value.passphrase734n;
        }

        emitTemplateUpdate("network_options", item);
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.success"),
            text: proxy.$t("dmp.networkOptions.messages.saveSuccess"),
            icon: "CheckIcon",
            variant: "success",
          },
        });
        return;
      }

      isSaving.value = true;

      store.commit("dmp_panels/setOperationActive", true);

      try {
        const dataToSend = {
          dhcp: networkOptions.value.dhcp ? "Y" : "N",
          localIp: networkOptions.value.localIp,
          gateway: networkOptions.value.gateway,
          subnetMask: networkOptions.value.subnetMask,
          dnsServer: networkOptions.value.dnsServer,
          listenPort734nOptions: networkOptions.value.listenPort734nOptions,
          ssid: networkOptions.value.ssid,
          wifiSecurityScheme: networkOptions.value.wifiSecurityScheme,
          wifiPassword: networkOptions.value.wifiPassword,
        };

        if (networkOptions.value.encryptionPassphrase) {
          dataToSend.encryptionPassphrase = networkOptions.value.encryptionPassphrase;
        }
        if (networkOptions.value.passphrase734n) {
          dataToSend.passphrase734n = networkOptions.value.passphrase734n;
        }
        if (networkOptions.value.ignoreBroadcastMessage) {
          dataToSend.ignoreBroadcastMessage = networkOptions.value.ignoreBroadcastMessage;
        }

        const response = await store.dispatch("dmp_panels/setNetworkOptions", {
          uuid: props.uuid,
          options: dataToSend,
        });

        if (response.success) {
          emit("operation-completed");
        }
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.success"),
            text: proxy.$t("dmp.networkOptions.messages.saveSuccess"),
            icon: "CheckIcon",
            variant: "success",
          },
        });
      } catch (error) {
        console.error("Error saving network options:", error);
        emit("operation-error", error);
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.error"),
            text: proxy.$t("dmp.networkOptions.messages.saveError"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        store.commit("dmp_panels/setOperationActive", false);
        isSaving.value = false;
      }
    };

    watch(
      () => props.networkOptionsData,
      (newData) => {
        if (newData) {
          loadNetworkOptions();
        }
      },
      { deep: true }
    );

    const registerTemplateSection = inject("registerTemplateSection", null);
    const unregisterTemplateSection = inject("unregisterTemplateSection", null);

    onMounted(() => {
      loadNetworkOptions();
      if (isTemplateMode.value && registerTemplateSection) {
        registerTemplateSection("network_options", {
          validate: validateTemplateSection,
          collect: buildTemplateItem,
        });
      }
    });

    onBeforeUnmount(() => {
      if (isTemplateMode.value && unregisterTemplateSection) {
        unregisterTemplateSection("network_options");
      }
    });

    return {
      isLoading,
      isSaving,
      networkOptions,
      securityOptions,
      saveNetworkOptions,
      refFormObserver,
      getValidationState,
      passphrase734nFieldType,
      wifiPasswordFieldType,
      t,
      isTemplateMode,
    };
  },
};
</script>

<style scoped lang="scss">
.border {
  border: 1px solid #ebe9f1;
}

.dark-layout {
  .border {
    border-color: #404656;
  }
}
</style>
